import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Typography, Box, IconButton, TextField, CircularProgress, Stack, Paper, Avatar } from "@mui/material";
import { CloudUpload, CloudUploadOutlined, InsertDriveFileOutlined, CloseRounded } from "@mui/icons-material";
import GlobalDialogContent from "../GlobalDialogContent";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/reducer/snackbarSlice";
import { useSearchParams } from "react-router-dom";
import { closeDialog } from "../../../redux/reducer/dialogSlice";
import { setModuleRefresh } from "../../../redux/reducer/registration/RefreshSlice";
import { TPMDocusUploadService } from "../../../utils/services/tri-party-mou/tri.party.mou.service";

interface FormValues {
    documentTitle: string;
    documentDescription: string;
    attachments: { file: File }[];
}

const allowedFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpeg",
    "image/png",
];

const TPMDocumentUpload: React.FC = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [fileNames, setFileNames] = useState<{ name: string; id: string }[]>([]);
    const [searchParams] = useSearchParams();
    const [isUploading, setIsUploading] = useState(false);
    const uuid = searchParams.get("uuid");

    const formik = useFormik<FormValues>({
        initialValues: {
            documentTitle: "",
            documentDescription: "",
            attachments: [],
        },
        validationSchema: Yup.object({
            documentTitle: Yup.string().required("Title is required"),
            documentDescription: Yup.string().required("Description is required"),
            attachments: Yup.array().min(1, "Please upload at least one document"),
        }),
        onSubmit: async (values) => {
            setIsUploading(true)
            const reqPayload = new FormData();
            reqPayload.append("title", values.documentTitle);
            reqPayload.append("description", values.documentDescription);
            reqPayload.append("tri_party_uuid", String(uuid));
            values.attachments.forEach((attachment) => {
                reqPayload.append("attachments", attachment.file);
            });
            const { code, data, message } = await TPMDocusUploadService(reqPayload);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || "Documents uploaded successfully." }));
                dispatch(setModuleRefresh({ moduleName: "TPMDocumentStoreRefresh", refresh: true }));
                dispatch(closeDialog());
            } else {
                dispatch(showSnackbar({ type: "error", message: message || "Documents upload error." }));
            }
            setIsUploading(false)
        },
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;

        const maxSize = 5 * 1024 * 1024;
        const validFiles: { file: File }[] = [];
        let hasSizeError = false;
        let hasTypeError = false;

        Array.from(event.target.files).forEach((file) => {
            if (!allowedFileTypes.includes(file.type)) {
                hasTypeError = true;
                return;
            }

            if (file.size > maxSize) {
                hasSizeError = true;
                return;
            }

            validFiles.push({ file });
        });

        if (hasSizeError) {
            dispatch(
                showSnackbar({
                    type: "error",
                    message: "File size should not exceed 5MB",
                })
            );
            event.target.value = "";
            return;
        }

        if (hasTypeError) {
            dispatch(
                showSnackbar({
                    type: "error",
                    message: "Only PDF, DOCX, XLSX, JPEG, and PNG files are allowed",
                })
            );
            event.target.value = "";
            return;
        }

        if (validFiles.length > 0) {
            const existingFiles = formik.values.attachments;
            const uniqueFiles = [...existingFiles, ...validFiles].filter(
                (fileWrapper, index, self) =>
                    index === self.findIndex((f) => f.file.name === fileWrapper.file.name)
            );

            formik.setFieldValue("attachments", uniqueFiles);
            setFileNames(uniqueFiles.map((f) => ({ name: f.file.name, id: f.file.name })));
        }

        event.target.value = "";
    };

    const handleRemoveFile = (fileName: string) => {
        const updatedFiles = formik.values.attachments.filter(
            (fileWrapper) => fileWrapper.file.name !== fileName
        );
        formik.setFieldValue("attachments", updatedFiles);
        setFileNames(updatedFiles.map((f) => ({ name: f.file.name, id: f.file.name })));
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <GlobalDialogContent
                dialogBody={
                    <Stack spacing={3} sx={{ mt: 1, mb: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: '#1E293B' }}>
                                Document Title <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="e.g., Q3 Progress Report"
                                name="documentTitle"
                                value={formik.values.documentTitle}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.documentTitle && Boolean(formik.errors.documentTitle)}
                                helperText={formik.touched.documentTitle ? formik.errors.documentTitle : undefined}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: '#1E293B' }}>
                                Description <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Provide brief context about the contents..."
                                multiline
                                rows={3}
                                name="documentDescription"
                                value={formik.values.documentDescription}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.documentDescription && Boolean(formik.errors.documentDescription)}
                                helperText={formik.touched.documentDescription ? formik.errors.documentDescription : undefined}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: '#1E293B' }}>
                                Attachments <span style={{ color: '#EF4444' }}>*</span>
                            </Typography>

                            {/* Drag and Drop Zone Style */}
                            <Box
                                component="label"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 2,
                                    border: '2px dashed',
                                    borderColor: formik.touched.attachments && formik.errors.attachments ? 'error.main' : '#CBD5E1',
                                    borderRadius: '16px',
                                    bgcolor: '#F8FAFC',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: '#3B82F6',
                                        bgcolor: '#EFF6FF'
                                    }
                                }}
                            >
                                <Avatar sx={{ bgcolor: '#DBEAFE', color: '#3B82F6', width: 56, height: 56, mb: 2 }}>
                                    <CloudUploadOutlined fontSize="medium" />
                                </Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                                    Click to browse or drag and drop
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center', maxWidth: 400 }}>
                                    Supports PDF, DOCX, XLSX, PNG, JPG (Max 5MB)
                                </Typography>
                                <input
                                    type="file"
                                    accept={allowedFileTypes.join(",")}
                                    multiple
                                    hidden
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                />
                            </Box>

                            {formik.touched.attachments && formik.errors.attachments && (
                                <Typography color="error" variant="caption" sx={{ mt: 0.75, display: 'block', ml: 1, fontWeight: 500 }}>
                                    {formik.errors.attachments as string}
                                </Typography>
                            )}
                        </Box>

                        {fileNames.length > 0 && (
                            <Stack spacing={1.25}>
                                {fileNames.map((file) => (
                                    <Paper
                                        key={file.id}
                                        elevation={0}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: 1,
                                            borderRadius: '12px',
                                            border: '1px solid',
                                            borderColor: '#E2E8F0',
                                            bgcolor: '#FFFFFF',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: '#CBD5E1',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ overflow: "hidden" }}>
                                            <Box sx={{ display: 'flex', p: 1, bgcolor: '#F1F5F9', borderRadius: '8px' }}>
                                                <InsertDriveFileOutlined sx={{ fontSize: 20, color: '#64748B' }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }} noWrap>
                                                {file.name}
                                            </Typography>
                                        </Stack>
                                        <IconButton
                                            onClick={() => handleRemoveFile(file.name)}
                                            size="small"
                                            sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: '#FEE2E2' } }}
                                        >
                                            <CloseRounded fontSize="small" />
                                        </IconButton>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                }
                dialogFooter={
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={isUploading ? <CircularProgress color="inherit" size={20} /> : <CloudUpload />}
                        disabled={isUploading}
                        sx={{
                            borderRadius: '10px',
                            px: 4,
                            py: 1,
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                            textTransform: 'none',
                            fontSize: '1rem'
                        }}
                    >
                        Upload Document
                    </Button>
                }
            />
        </form>
    );
};

export default TPMDocumentUpload;
